import { Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import {
    EntitySaveOptions,
    IManageEntityRoutes,
    SafeAny,
} from '@pf/shared-common';
import { Observable, of } from 'rxjs';
import { asArraySafe } from '@pf/shared-utility';
import {
    ItemChargeCreate,
    ItemChargeDataService,
    ItemChargeDto,
    ItemChargeMapper,
    ItemChargeRead,
    ItemChargeSearchParams,
    ItemChargeStore,
    LaneRateDto,
} from '@pf/platform-loads/domain';
import { ItemChargeViewModelBody } from '@control-tower/platform-loads';

@Injectable()
export class ItemChargeFacade extends AbstractManageEntityFacade<
    ItemChargeDto,
    ItemChargeRead,
    ItemChargeCreate,
    ItemChargeSearchParams
> {
    nameField: keyof ItemChargeDto = 'name';

    constructor(
        dataService: ItemChargeDataService,
        store: ItemChargeStore,
        mapper: ItemChargeMapper
    ) {
        super(
            dataService,
            null as unknown as IManageEntityRoutes,
            store,
            mapper
        );
    }

    override add() {
        this.store.resetActiveEntity();
    }

    override edit(entityId: string) {
        this.store.setActiveEntity(entityId);
    }

    override manage() {
        this.store.resetActiveEntity();
    }

    override save$(
        dto: ItemChargeDto,
        options?: EntitySaveOptions<ItemChargeDto>
    ): Observable<ItemChargeDto> {
        return super.save$(dto, {
            ...(options || {}),
            postSave: savedDto => {
                savedDto.rateId = dto.rateId;
            },
        });
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ItemChargeDto, string>> {
        return { id: searchText };
    }

    checkIfChargeExists(rate: LaneRateDto, dto: ItemChargeDto) {
        if (dto.id) {
            return of(false);
        }
        return of(
            asArraySafe(rate.itemCharges).some(
                c =>
                    c.chargeType?.id === dto.chargeType?.id &&
                    c.accountType === dto.accountType &&
                    c.unitOfMeasure === dto.unitOfMeasure
            )
        );
    }

    override applyResourceAuth(body: ItemChargeViewModelBody): void {
        body.resourceAuth = { allAuthorized: true };
    }
}
